import { ChartPanel } from '@/features/report-detail/components/chart-panel.js';
import { ResultsTable } from '@/features/report-detail/components/results-table.js';
import { cn } from '@/lib/utils.js';
import type { VisualizationKind } from '@quartup/reports-shared';

// ---------------------------------------------------------------------------
// KPI value formatter
// ---------------------------------------------------------------------------

type KpiFormat = 'money' | 'percent' | 'number' | 'integer' | 'text';

function formatKpiValue(value: unknown, format: KpiFormat): string {
  if (value === null || value === undefined) return '—';
  if (format === 'text') return String(value);
  const num = typeof value === 'number' ? value : Number(value);
  if (Number.isNaN(num)) return String(value);
  switch (format) {
    case 'money':
      return new Intl.NumberFormat('es-ES', {
        style: 'currency',
        currency: 'EUR',
        maximumFractionDigits: 2,
      }).format(num);
    case 'percent':
      return new Intl.NumberFormat('es-ES', {
        style: 'percent',
        maximumFractionDigits: 1,
      }).format(num / 100);
    case 'integer':
      return new Intl.NumberFormat('es-ES', { maximumFractionDigits: 0 }).format(num);
    default:
      return new Intl.NumberFormat('es-ES', { maximumFractionDigits: 2 }).format(num);
  }
}

// ---------------------------------------------------------------------------
// KPI widget
// ---------------------------------------------------------------------------

interface KpiConfig {
  value_field?: string | undefined;
  format?: KpiFormat | undefined;
  delta_field?: string | undefined;
}

interface KpiWidgetProps {
  rows: ReadonlyArray<Record<string, unknown>>;
  kpiConfig: KpiConfig;
}

function KpiWidget({ rows, kpiConfig }: KpiWidgetProps) {
  const row = rows[0] ?? {};
  const { value_field = 'value', format = 'number', delta_field } = kpiConfig;
  const rawValue = row[value_field];
  const formatted = formatKpiValue(rawValue, format);

  const delta =
    delta_field !== undefined && row[delta_field] !== undefined
      ? (Number(row[delta_field]) ?? null)
      : null;
  const isPositive = delta !== null && delta > 0;
  const isNegative = delta !== null && delta < 0;

  return (
    <div className="flex flex-col items-center justify-center py-4" data-testid="kpi-widget">
      <p
        className={cn(
          'text-4xl font-bold tabular-nums',
          isPositive && 'text-[var(--color-qup-primary)]',
          isNegative && 'text-[var(--color-qup-text-muted-dark)]',
          !isPositive && !isNegative && 'text-[var(--color-qup-text-dark)]',
        )}
      >
        {formatted}
      </p>
      {delta !== null && (
        <p
          className={cn(
            'mt-2 text-sm font-medium',
            isPositive && 'text-green-400',
            isNegative && 'text-red-400',
          )}
          aria-label={`Delta: ${delta > 0 ? '+' : ''}${delta}`}
        >
          {delta > 0 ? '▲' : '▼'} {Math.abs(delta).toFixed(1)}%
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Table widget
// ---------------------------------------------------------------------------

interface TableWidgetProps {
  rows: ReadonlyArray<Record<string, unknown>>;
  maxRows?: number;
}

function TableWidget({ rows, maxRows = 10 }: TableWidgetProps) {
  const displayRows = rows.slice(0, maxRows);
  const columns =
    displayRows.length > 0
      ? Object.keys(displayRows[0] ?? {}).map((key) => ({
          key,
          type: 'string' as const,
          label: { es: key, en: key },
          sortable: false,
        }))
      : [];

  if (columns.length === 0) {
    return (
      <p className="py-4 text-center text-sm text-[var(--color-qup-text-muted-dark)]">
        No data available
      </p>
    );
  }

  return (
    <div data-testid="table-widget">
      <ResultsTable columns={columns} rows={displayRows} totalRows={rows.length} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Chart widget
// ---------------------------------------------------------------------------

interface ChartWidgetConfig {
  category_key?: string | undefined;
  value_key?: string | undefined;
  visualization?: VisualizationKind | undefined;
}

interface ChartWidgetProps {
  rows: ReadonlyArray<Record<string, unknown>>;
  config?: ChartWidgetConfig;
  visualization: VisualizationKind;
}

function ChartWidget({ rows, config, visualization }: ChartWidgetProps) {
  const viz = config?.visualization ?? visualization;
  return (
    <div data-testid="chart-widget">
      <ChartPanel
        rows={rows}
        visualization={viz}
        {...(config?.category_key !== undefined ? { categoryKey: config.category_key } : {})}
        {...(config?.value_key !== undefined ? { valueKey: config.value_key } : {})}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Public widget renderer dispatcher
// ---------------------------------------------------------------------------

export type WidgetType = 'kpi' | 'chart' | 'table';

interface WidgetRendererProps {
  type: VisualizationKind;
  rows: ReadonlyArray<Record<string, unknown>>;
  /** viz_config from the widget definition */
  vizConfig?: Record<string, unknown> | undefined;
}

export function WidgetRenderer({ type, rows, vizConfig }: WidgetRendererProps) {
  if (type === 'kpi') {
    const valueField =
      typeof vizConfig?.value_field === 'string' ? vizConfig.value_field : undefined;
    const fmt = typeof vizConfig?.format === 'string' ? (vizConfig.format as KpiFormat) : undefined;
    const deltaField =
      typeof vizConfig?.delta_field === 'string' ? vizConfig.delta_field : undefined;
    const kpiConfig: KpiConfig = {
      ...(valueField !== undefined ? { value_field: valueField } : {}),
      ...(fmt !== undefined ? { format: fmt } : {}),
      ...(deltaField !== undefined ? { delta_field: deltaField } : {}),
    };
    return <KpiWidget rows={rows} kpiConfig={kpiConfig} />;
  }

  if (type === 'table') {
    const maxRows = typeof vizConfig?.max_rows === 'number' ? vizConfig.max_rows : 10;
    return <TableWidget rows={rows} maxRows={maxRows} />;
  }

  // chart types: bar, line, area, pie, donut, gauge, scatter, heatmap
  const categoryKey =
    typeof vizConfig?.category_key === 'string' ? vizConfig.category_key : undefined;
  const valueKey = typeof vizConfig?.value_key === 'string' ? vizConfig.value_key : undefined;
  const vizKind =
    typeof vizConfig?.visualization === 'string'
      ? (vizConfig.visualization as VisualizationKind)
      : undefined;
  const chartConfig: ChartWidgetConfig = {
    ...(categoryKey !== undefined ? { category_key: categoryKey } : {}),
    ...(valueKey !== undefined ? { value_key: valueKey } : {}),
    ...(vizKind !== undefined ? { visualization: vizKind } : {}),
  };
  return <ChartWidget rows={rows} config={chartConfig} visualization={type} />;
}
